'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { isEmpty, toNumber, isNaN } from 'lodash';

import { Input } from '@repo/ui';
import { numberUtil } from '@xtnd/web/shared/utils';

/**
 * Props for the AmountInput component.
 */
export interface AmountInputProps {
  /** Current value (stored as string internally for input control) */
  value: number;
  /** Callback when value changes */
  onChange: (value: number) => void;
  /** Optional callback to report validation state to parent */
  onValidChange?: (isValid: boolean) => void;
  /** Input label */
  label: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Minimum allowed amount */
  minAmount?: number;
  /** Maximum allowed amount */
  maxAmount?: number;
  /** Optional className for the input container */
  className?: string;
  /** Optional custom helper text renderer. Receives the remaining balance as argument. */
  renderHelperText?: (remainingBalance: number) => React.ReactNode;
}

/**
 * AmountInput Component
 *
 * A reusable amount input with validation. Follows the same pattern as AppealModal:
 * - Uses string internally for input control
 * - Normalizes input to remove invalid characters
 * - Step-by-step validation with clear error messages
 */
export function AmountInput({
  value,
  onChange,
  onValidChange,
  label,
  placeholder,
  required,
  minAmount = 0,
  maxAmount = 0,
  className = '',
  renderHelperText,
}: AmountInputProps) {
  // Store as string for input control (like AppealModal)
  const [displayValue, setDisplayValue] = useState<string>(value.toString());
  const [error, setError] = useState<string>('');

  /**
   * Sync display value when parent value changes (e.g., form reset/init)
   */
  useEffect(() => {
    setDisplayValue(value.toString());
  }, [value]);

  /**
   * Handles changes to the input field for the amount.
   * Copied from AppealModal's handleAmountChange implementation.
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const normalized = numberUtil.normalizeNumericString(raw);
      const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;

      setDisplayValue(normalized);

      // Validate format
      if (!AMOUNT_REGEX.test(normalized)) {
        setError('Amount must contain only numbers');
        onChange(0);
        onValidChange?.(false);
        return;
      }

      // Allow clearing the field
      if (isEmpty(normalized)) {
        setDisplayValue(minAmount.toString());
        setError('');
        onChange(minAmount);
        onValidChange?.(true);
        return;
      }

      const numericValue = toNumber(normalized);

      // Validate NaN
      if (isNaN(numericValue)) {
        setError('Invalid number');
        onChange(0);
        onValidChange?.(false);
        return;
      }

      // Validate range: must be greater than minAmount
      if (numericValue < minAmount) {
        setError(`Amount must be greater than ${minAmount}`);
        onChange(numericValue);
        onValidChange?.(false);
        return;
      }

      // Validate range: cannot exceed max
      if (numericValue > maxAmount) {
        setError(`Amount cannot exceed ${numberUtil.formatCurrency(maxAmount)}`);
        onChange(numericValue);
        onValidChange?.(false);
        return;
      }

      // Valid
      setError('');
      onChange(numericValue);
      onValidChange?.(true);
    },
    [onChange, onValidChange, minAmount, maxAmount]
  );

  /**
   * Validate initial value on mount
   */
  useEffect(() => {
    if (value === 0) {
      onValidChange?.(false);
      return;
    }

    if (value < minAmount) {
      setError(`Amount must be greater than ${minAmount}`);
      onValidChange?.(false);
      return;
    }

    if (value > maxAmount) {
      setError(`Amount cannot exceed ${numberUtil.formatCurrency(maxAmount)}`);
      onValidChange?.(false);
      return;
    }

    setError('');
    onValidChange?.(true);
  }, [value, minAmount, maxAmount, onValidChange]);

  const remainingBalance = maxAmount - value;
  const showHelperText = value > 0 && !error && renderHelperText;

  return (
    <div className="space-y-2">
      <Input
        id="amount"
        type="number"
        label={label}
        value={displayValue}
        min="0"
        max={maxAmount}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        error={error}
        fullWidth
        className={className}
      />
      {showHelperText && renderHelperText(remainingBalance)}
    </div>
  );
}

export default AmountInput;
